import { html } from 'lit-element';

import { LightLitElement } from '@elab/components';
import Breakpoints from '@elab/core/utils/Breakpoints';
import connect from '@elab/core/store/connect';
import { actions as uiActions } from '@elab/core/store/ui';
import { actions as globalActions } from '@elab/core/store/global';
import slotsUtils from '@elab/core/utils/ui/slotsUtils';
import { link } from '@elab/core/utils/htmlTemplates';

/**
 * Component is used to render a group of links of the My Account navigation.
 * It is visible only on desktop (as an accordion) and writes it's data to 'ui.accountNav' store.
 *
 * @author Roberto Schiavone
 * @attr {String} [title]
 * HTML Structure
 * <e2-my-account-navigation-link url="/my-account-navigation.html"
 *                                title="Your Overview"></e2-my-account-navigation-link>
 * <e2-my-account-navigation-group title="Your Advantage">
 *     <e2-my-account-navigation-link url="#"
 *                                    title="Your extra offers"></e2-my-account-navigation-link>
 *     <e2-my-account-navigation-link url="#" title="Your card"></e2-my-account-navigation-link>
 *     <e2-my-account-navigation-link url="#"
 *                                    title="Your Baby Benefit"></e2-my-account-navigation-link>
 *     <e2-my-account-navigation-link url="#"
 *                                    title="Your Loyalty Points"></e2-my-account-navigation-link>
 *     <e2-my-account-navigation-link url="#" title="Your Kruidvat App"></e2-my-account-navigation-link>
 * </e2-my-account-navigation-group>
 * */

class MyAccountNavigationGroup extends LightLitElement {
    static get properties() {
        return {
            title: { type: String },
            addClass: { type: String },
            titleUrl: { type: String, attribute: 'accordion-title-url' },
            classModifier: {
                type: String,
                attribute: 'class-modifier',
            },
            iconClassName: {
                type: String,
                attribute: 'icon-class-name',
            },
            isDefaultCollapsible: {
                type: Boolean,
                attribute: 'is-default-collapsible',
            },
            open: { type: String, attribute: false },
        };
    }

    constructor() {
        super();
        this.title = '';
        this.addClass = '';
        this.titleUrl = '';
        this.classModifier = '';
        this.open = false;
        this.isDefaultCollapsible = false;
    }

    firstUpdated() {
        slotsUtils.emulateSlots(this);
        // TODO: legacy KV code, mobile my account menu logic need to be refactored
        setTimeout(this.onComponentReady.bind(this), 0);
    }

    renderAccordionTitle() {
        return html`
            ${this.isOpen()
                ? link(
                      { href: this.titleUrl, target: '_self' },
                      html`
                          <h5 class="my-account-navigation__title">
                              ${this.title}
                          </h5>
                      `,
                  )
                : html`
                      <h5 class="my-account-navigation__title">
                          ${this.title}
                      </h5>
                  `}
        `;
    }

    isOpen = () => {
        return !!(Breakpoints.desktopDevice && this.titleUrl) || this.open;
    };

    render() {
        return html`
            <e2-accordion
                arrow-close-classes="my-account-navigation__arrow"
                arrow-open-classes="my-account-navigation__arrow my-account-navigation__arrow--expanded"
                active-xxl
                active-xl
                active-lg
                active-md
                active-sm
                ?open=${this.isOpen()}
                ?default-open=${this.isOpen()}
                ?non-collapsible=${this.isOpen() && !this.isDefaultCollapsible}
                add-class="
                ${this.addClass ? `${this.addClass} ${this.addClass}--${this.classModifier}` : ''}
                ${this.open && this.addClass ? ` ${this.addClass}--active` : ''}"
            >
                <div v-slot="title" class="my-account-navigation__title-wrapper">
                    <span class="my-account-navigation__group-icon">
                        <i class="${this.iconClassName}"></i>
                    </span>
                    ${this.renderAccordionTitle()}
                </div>
                <nav
                    class="my-account-navigation__nav ${this.classModifier
                        ? `my-account-navigation__nav--${this.classModifier}`
                        : ''}"
                    v-slot="content"
                >
                    <v-slot name="menu-items" class="emulate-slot"></v-slot>
                </nav>
            </e2-accordion>
        `;
    }

    onComponentReady() {
        let links = this.querySelector('v-slot')
            .assignedNodes()
            .filter(node => !!node.style)
            .map(link => ({
                title: link.title,
                url: link.url,
            }));

        if (links) {
            this.dispatchEvent(
                new CustomEvent('account-navigation-add', {
                    detail: {
                        title: this.title,
                        children: links,
                    },
                }),
            );
        }
    }
}

const mapDispatchToEvents = dispatch => ({
    'account-navigation-add': ({ detail }) => dispatch(uiActions.addAccountNavigation(detail)),
    redirect: e => dispatch(globalActions.redirect(e.detail)),
});

export default () =>
    customElements.define(
        'e2-my-account-navigation-group',
        connect(null, mapDispatchToEvents)(MyAccountNavigationGroup),
    );
